import {
  useMutation,
  type MutationHookOptions,
  type MutationTuple,
  type TypedDocumentNode,
} from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { AppShow__ContentItem } from '~/components/AppForm';
import { useNotifications, type ApiOptions } from '../helpers';
import { RenameContentItemDocument, UpdateContentItemDocument } from './update.generated';

export { RenameContentItemDocument, UpdateContentItemDocument };

const useUpdate = <T, U>(
  document: TypedDocumentNode<T, U>,
  options: MutationHookOptions<T, U>,
): MutationTuple<T, U> => {
  const { t } = useTranslation();
  return useMutation(document, {
    ...useNotifications(document, {
      failure: t('api.content_items.update_error'),
      success: t('api.content_items.update_success'),
    }),
    ...options,
  });
};

export const useUpdateContentItem = (options: ApiOptions<typeof UpdateContentItemDocument> = {}) =>
  useUpdate(UpdateContentItemDocument, {
    // we need to manually set the new thumbnailUri
    // because what would be returned from the server
    // would be incorrect until the thumbnail job has processed
    update: (cache, _, { variables }) => {
      const id = variables?.contentItemId;
      const patch = variables?.patch;
      if (!id || !patch?.thumbnailUri) return;

      const cachedItem = cache.readFragment({
        id: cache.identify({ __typename: 'ContentItem', id }),
        fragment: AppShow__ContentItem,
        fragmentName: 'AppShow__ContentItem',
      });

      if (!cachedItem) return;

      cache.writeFragment({
        id: cache.identify({ __typename: 'ContentItem', id }),
        fragment: AppShow__ContentItem,
        fragmentName: 'AppShow__ContentItem',
        data: {
          ...cachedItem,
          thumbnailUri: patch.thumbnailUri,
        },
      });
    },
    ...options,
  });

export const useRenameContentItem = (options: ApiOptions<typeof RenameContentItemDocument> = {}) =>
  useUpdate(RenameContentItemDocument, options);
